import { useSyncExternalStore } from "react";

/** Vendor prefixes still needed for the Safari versions the canvas supports. */
const FULLSCREEN_PREFIXES = ["", "webkit", "moz", "ms"];

type PrefixedDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

function getFullscreenElement(): Element | null {
  const doc = document as PrefixedDocument;
  return (
    doc.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.mozFullScreenElement ?? doc.msFullscreenElement ?? null
  );
}

function subscribe(onChange: () => void) {
  for (const prefix of FULLSCREEN_PREFIXES) {
    document.addEventListener(`${prefix}fullscreenchange`, onChange);
  }
  return () => {
    for (const prefix of FULLSCREEN_PREFIXES) {
      document.removeEventListener(`${prefix}fullscreenchange`, onChange);
    }
  };
}

/**
 * Whether the document is currently presented fullscreen.
 *
 * Read-only counterpart to Mantine's `useFullscreen`, for chrome that reacts to fullscreen without owning the toggle.
 * The snapshot is read from the DOM on every render, so it is correct even when the component mounts while already
 * fullscreen, and it tracks exits made through the browser's own controls (Esc, F11).
 */
export function useIsFullscreen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => getFullscreenElement() !== null,
    () => false,
  );
}
